import { Component, OnInit, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { EditUnplacedTicketsComponent } from '../../../dialogs/edit-unplaced-tickets/edit-unplaced-tickets.component';
import {ConfirmService, HttpLoaderService} from '@tymes4-shared';
import { ChangeUnplacedContingentsComponent } from '../../../dialogs/change-unplaced-contingents/change-unplaced-contingents.component';
import { EditUnplacedEventCapacityComponent } from '../../../dialogs/edit-unplaced-event-capacity/edit-unplaced-event-capacity.component';
import { TranslateService } from '@ngx-translate/core';
import { CreateBarcodesExportArgs, Event, EventPlacementService, GetExternalBarcodesCountResponse, LongRunningOperationService, VenueBuildingBlockService } from '../../../api';
import { NotificationCommunicationService} from '@tymes4-shared';
import { UploadBarcodesImportfileComponent } from '../../../dialogs/upload-barcodes-importfile/upload-barcodes-importfile.component';
import * as FileSaver from 'file-saver';
import { GenericErrorHandler } from '../../../helpers/generic-error-handler';

@Component({
    selector: 'app-unplaced-ticket-list',
    templateUrl: './unplaced-ticket-list.component.html',
    styleUrls: ['./unplaced-ticket-list.component.scss'],
    standalone: false
})
export class UnplacedTicketListComponent implements OnInit {

  constructor(private dialog: MatDialog,
              private confirmService: ConfirmService,
              private venue : VenueBuildingBlockService,
              private translate: TranslateService,
              private eventPlacementService: EventPlacementService,
              private longRunningOperationService: LongRunningOperationService,
              private notificationCommunicationService: NotificationCommunicationService,
              private loader: HttpLoaderService,
              private errorHandler: GenericErrorHandler,
) { }


  @ViewChild('pagerComponent') pagerComponent;
  @ViewChild('searchComponent') searchComponent;

  parentVBB;
  selectedTicketType;
  existingEndpoints = [];
  filteredEndpoints = [];
  priceCategories = [];
  searchText = "";
  eventPlacementEventId: number | null = null;
  showContingentEditButton = false;
  allowContingentEdit = false;
  minEventCapacity = 0;
  totalEndpointCount: number | null = null;
  unsoldExternalBarcodesCount: number = 0;
  soldExternalBarcodesCount: number = 0;

  @Output() onRefreshRequired = new EventEmitter();

  @Input()
  set eventId(eId: number) {
    this.eventPlacementEventId = eId;
  }

  ngOnInit() {
    this.loader.open();
    this.venue.allowContingentsForUnplacedSections().subscribe(setting => {
      this.showContingentEditButton = setting;
      this.loadBarcodesCount();
    });
  }

  @Input()
  set parent(vbb) {

    this.parentVBB = vbb;

    if (this.eventPlacementEventId == null) {
      //vbb, if events are linked it is not possible to change contingents
      this.venue.getLinkedEvents(this.parentVBB.Id).subscribe((events: Event[]) => {
        this.allowContingentEdit = (events == null || events.length == 0);
      });
    }
    else {
      this.allowContingentEdit = true;
    }
  }

  @Input()
  set endpoints(eps) {
    this.existingEndpoints = eps;
    this.filteredEndpoints = this.existingEndpoints;
  }

  @Input()
  set totalEndpoints(eps: any[]) {
    this.totalEndpointCount = eps != null ? eps.length : null;
  }

  @Input()
  set ticketType(tt) {
    this.selectedTicketType = tt;
  }
  @Input()
  set soldTickets(sold: number) {
    this.minEventCapacity = sold;
  }

  delete(id) {
    this.confirmService.confirm({title: this.translate.instant('GENERIC.CONFIRM.CONFIRM'),
      message: this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.CONFIRM.WARNING.MESSAGE.DELETE-TICKET')})
    .subscribe((result: boolean) => {
      if (result) {
        this.venue.deleteVenueBuildingBlock(id).subscribe();
      }
    });
  }

  manageContingents() {
    const title: string = this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.DIALOG.TITLE.EDIT-QUOTAS', {parentVBBName: this.parentVBB.Name})
    const dialogRef: MatDialogRef<any> = this.dialog.open(ChangeUnplacedContingentsComponent, {
      width: '720px',
      disableClose: true,
      data: { title: title, totalPlaces: this.existingEndpoints.length, sectionId: this.parentVBB.Id, eventPlacementEventId: this.eventPlacementEventId, parentVBBHasPlacements: this.parentVBB.HasPlacements }
    })
    dialogRef.afterClosed()
      .subscribe();
  }

  canChangeEventCapacity(): boolean {
    return this.eventPlacementEventId !== null && this.maxEventCapacity() >= this.minEventCapacity;
  }

  maxEventCapacity(): number {
    // Use total (including blocked) when available so capacity reflects the full pool.
    return this.totalEndpointCount ?? this.existingEndpoints.length;
  }

  changeEventCapacity() {
    if (!this.canChangeEventCapacity()) {
      return;
    }

    const title: string = this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.DIALOG.TITLE.EDIT-AMOUNT', {parentVBBName: this.parentVBB.Name})

    const dialogRef: MatDialogRef<any> = this.dialog.open(EditUnplacedEventCapacityComponent, {
      width: '720px',
      disableClose: true,
      data: {
        title: title,
        parentId: this.parentVBB.Id,
        ticketTypeId: this.selectedTicketType.Id,
        eventPlacementEventId: this.eventPlacementEventId,
        currentAvailableTickets: this.existingEndpoints.length,
        minCapacity: this.minEventCapacity,
        maxCapacity: this.maxEventCapacity(),
        unsoldExternalBarcodesCount: this.unsoldExternalBarcodesCount,
        soldExternalBarcodesCount: this.soldExternalBarcodesCount
      }
    });

    dialogRef.afterClosed()
      .subscribe((endpoints: any[]) => {
        if (endpoints) {
          this.existingEndpoints = endpoints.filter(ep => !ep.Blocked);
          this.filteredEndpoints = this.existingEndpoints;
          this.onRefreshRequired.emit();
        }
      });
  }

  addOrUpdateTickets(idx, isNew, ep) {
    let data: any = null;

    //set the required data
    data = {};
    data.SelectedIdx = idx;
    data.ParentId = this.parentVBB.Id;
    data.TicketTypeId = this.selectedTicketType.Id;
    data.currentTicketAmount = this.existingEndpoints.length;

    let title:string = this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.FORM.TITLE.TICKETS-FORM-TYPE', {selectedTicketTypeName: this.selectedTicketType.Name})

    if (isNew) title += this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.FORM.TITLE.ADD')
    else title = this.translate.instant('COMPONENTS.VENUE-DEFENITION.UNPLACED-TICKET-LIST.FORM.TITLE.EDIT');


    let dialogRef: MatDialogRef<any> = this.dialog.open(EditUnplacedTicketsComponent, {
      width: '720px',
      disableClose: true,
      data: { title: title, payload: data, isNew: isNew }
    })
    dialogRef.afterClosed()
      .subscribe(saved => {
        if (saved) {
          this.onRefreshRequired.emit();
        }
      }
    )
  }

  downloadBarcodesImportFile(){
      this.loader.open();
      const args : CreateBarcodesExportArgs = {
        EventId: this.eventPlacementEventId,
        TicketTypeId: this.selectedTicketType.Id,
      };
  
  
      this.eventPlacementService.createBarcodesImportFile(args).subscribe((data: Blob) => {
        this.loader.close();
        FileSaver.saveAs(data, this.translate.instant('import_external_barcodes_' + this.eventPlacementEventId + '_' + this.selectedTicketType.Id) + '.xlsx');
      },
      error => { this.errorHandler.handle(error);}
      );
    }

  importBarcodes() {
      let title = this.translate.instant('REPURCHASE.RESERVATIONS.IMPORT.BARCODES.FILE');
      const args = {
        EventId: this.eventPlacementEventId,
        ParentId: this.parentVBB.Id,
        TicketTypeId: this.selectedTicketType.Id,
      };
      let dialogRef: MatDialogRef<any> = this.dialog.open(UploadBarcodesImportfileComponent, {
        width: '720px',
        disableClose: true,
        data: {title: title, payload: args}
      })
      dialogRef.afterClosed()
        .subscribe(lro => {
            if (lro!=null) {
              this.notificationCommunicationService.updateNotificationsAndLROS();
              this.waitForBarcodeImportCompletion(lro.Id);
            }
          }
        )
    }

    private waitForBarcodeImportCompletion(lroId: number): void {
      this.longRunningOperationService.getLongRunningOperation(lroId).subscribe(lro => {
        if (lro.IsPending === true || lro.IsQueued === true) {
          setTimeout(() => this.waitForBarcodeImportCompletion(lroId), 1000);
        } else {
          this.loadBarcodesCount();
        }
      });
    }

    resetBarcodes(){
      if (this.eventPlacementEventId === null) {
        return;
      }

      this.loader.open();

      this.eventPlacementService.resetBarcodes(this.eventPlacementEventId, this.selectedTicketType.Id, this.parentVBB.Id).subscribe(() => {
        this.loadBarcodesCount();
        this.loader.close();
        this.notificationCommunicationService.updateNotificationsAndLROS();
      },
      error => { this.errorHandler.handle(error);}
      );
    }

    loadBarcodesCount(){
      if (this.eventPlacementEventId !== null && this.selectedTicketType && this.parentVBB) {
        this.loader.open();
        this.eventPlacementService.getExternalBarcodesCount(this.eventPlacementEventId, this.selectedTicketType.Id, this.parentVBB.Id)
          .subscribe((count: GetExternalBarcodesCountResponse) => {
            this.unsoldExternalBarcodesCount = count.UnsoldEpsWithExternalBarcode ?? 0;
            this.soldExternalBarcodesCount = count.SoldEpsWithExternalBarcode ?? 0;
            this.loader.close();
          });
      }
    }
}
